import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, LinearProgress, Paper, Stack, TextField, Typography } from '@mui/material';

function secureId() {
  if (!window.crypto?.getRandomValues) throw new Error('Secure scan IDs unavailable. Use a supported browser over HTTPS.');
  return Array.from(window.crypto.getRandomValues(new Uint8Array(16)), (n) => n.toString(16).padStart(2, '0')).join('');
}

function beep(ok) {
  try {
    const Context = window.AudioContext || window.webkitAudioContext;
    if (!Context) return;
    const context = new Context();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = ok ? 880 : 220;
    gain.gain.value = 0.04;
    oscillator.connect(gain); gain.connect(context.destination);
    oscillator.start(); oscillator.stop(context.currentTime + 0.08);
    oscillator.onended = () => context.close();
  } catch (_) { /* Visible status remains authoritative. */ }
}

export default function ScannerInput({ title, helper, disabled, onScan, progress, packageMode = false, onPending }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState(null);
  const input = useRef(null);

  const mounted = useRef(true);
  const flight = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; onPending?.(false); }; }, [onPending]);
  useEffect(() => { onPending?.(Boolean(pending) || busy); }, [pending, busy, onPending]);
  useEffect(() => {
    const active = document.activeElement;
    if (!disabled && !busy && !pending && (!active || active === document.body || active === input.current || active.disabled)) input.current?.focus();
  }, [disabled, busy, pending, status]);

  async function submit(retry = false) {
    if (disabled || flight.current || (!retry && pending)) return;
    const token = retry ? pending?.scan_value : value.trim();
    if (!token) return;
    const request = retry ? pending : { scan_value: token, scan_event_id: secureId(), scanner_kind: 'KEYBOARD_WEDGE' };
    flight.current = true; setBusy(true); setStatus(null);
    if (!retry) setPending(request);
    try {
      const result = await onScan(request);
      if (!mounted.current) return;
      const ok = result.accepted === true;
      setStatus({ ok, text: result.status || (ok ? 'ACCEPTED' : 'NOT ACCEPTED') });
      beep(ok);
      setPending(null); setValue('');
    } catch (err) {
      if (!mounted.current) return;
      if (err?.status >= 400 && err?.status < 500) setPending(null);
      setStatus({ ok: false, text: err?.message || 'Scan outcome unknown. Retry the same scan.' });
    } finally { flight.current = false; if (mounted.current) setBusy(false); }
  }

  const rows = progress?.items || [];
  const accepted = rows.reduce((total, row) => total + (row.accepted_scans || 0), 0);
  const required = rows.reduce((total, row) => total + (row.required_scans || 0), 0);
  const complete = packageMode ? progress?.final_package_verified === true : progress?.item_scan_complete === true;

  return <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, minWidth: 0, bgcolor: 'background.paper' }}>
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
          {helper && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{helper}</Typography>}
        </Box>
        <Chip size="small" color={complete ? 'success' : 'primary'} variant={complete ? 'filled' : 'outlined'} label={complete ? 'Verified' : packageMode ? 'QR required' : required ? `${accepted}/${required}` : 'Scan required'} />
      </Stack>

      {!packageMode && required > 0 && <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ mb: 0.65 }}>
          <Typography variant="caption" color="text.secondary">Verification progress</Typography>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>{accepted} of {required}</Typography>
        </Stack>
        <LinearProgress aria-label="Overall scan progress" variant="determinate" value={Math.min(100, accepted / required * 100)} sx={{ height: 9, borderRadius: 99 }} />
      </Box>}

      <Box sx={{
        borderRadius: 3, border: '1px solid', borderColor: complete ? 'success.light' : 'primary.light',
        bgcolor: complete ? 'success.50' : 'rgba(239,246,255,0.75)', px: { xs: 1.5, sm: 2 }, py: { xs: 2, sm: 2.5 }, textAlign: 'center',
      }}>
        <Typography aria-hidden="true" sx={{ fontSize: { xs: 34, sm: 42 }, lineHeight: 1, letterSpacing: '-0.16em', fontFamily: 'monospace', pr: 1 }}>▌▌ ▌ ▌▌▌</Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 1 }}>{complete ? packageMode ? 'Package verified' : 'Verification complete' : packageMode ? 'Scan package QR' : 'Waiting for scan…'}</Typography>
        <Typography variant="caption" color="text.secondary">{packageMode ? 'Use the current package identity only.' : 'Scanner input remains bound to this package.'}</Typography>
      </Box>

      {packageMode && progress?.final_package_verified && <Alert severity="success">Current package QR verified for this package and revision.</Alert>}
      {status && <Alert severity={status.ok ? 'success' : 'error'} aria-live="assertive">{status.text}</Alert>}

      <TextField fullWidth inputRef={input} label={packageMode ? 'Scan current package QR' : 'Scan item barcode / label'} value={value}
        disabled={disabled || busy || Boolean(pending)} autoComplete="off" inputProps={{ 'data-scanner-input': 'true' }}
        onChange={(event) => setValue(event.target.value)} onKeyDown={(event) => {
          if (event.key === 'Enter') { event.preventDefault(); submit(false); }
        }} helperText="Keep this field focused for a keyboard-wedge scanner. Enter submits one fresh scan cycle." />

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
        <Button size="large" variant="outlined" disabled={disabled || busy || Boolean(pending)} onClick={() => input.current?.focus()}>Focus scanner</Button>
        <Button size="large" variant="contained" sx={{ flex: 1 }} disabled={disabled || busy || Boolean(pending) || !value.trim()} onClick={() => submit(false)}>Submit scan</Button>
        {pending && <Button size="large" variant="outlined" disabled={disabled || busy} onClick={() => submit(true)}>Retry same scan</Button>}
      </Stack>

      {progress && rows.length > 0 && <Box component="details">
        <Typography component="summary" variant="body2" sx={{ cursor: 'pointer', fontWeight: 650, color: 'text.secondary', userSelect: 'none' }}>Scan progress details</Typography>
        <Stack spacing={1} sx={{ pt: 1 }}>{rows.map((row) => <Stack key={row.source_order_item_id} spacing={0.25}>
          <Typography variant="body2">Line {row.source_order_item_id} · {row.profile_code} · {row.accepted_scans}/{row.required_scans} scans</Typography>
          <LinearProgress variant="determinate" value={row.required_scans ? Math.min(100, row.accepted_scans / row.required_scans * 100) : 0} />
          <Typography variant="caption" color="text.secondary">Sale units {row.verified_sale_units}/{row.expected_sale_units} · components {row.verified_components}/{row.expected_components}{row.manual_component_confirmation_required ? ' · internal component count still needs manual confirmation' : ''}</Typography>
        </Stack>)}</Stack>
      </Box>}
    </Stack>
  </Paper>;
}
